import { useContext } from 'react';
import {
  formatThemeText,
  getHeadingTextTransform,
  toSentenceCase,
  usesSentenceCase,
} from '../../../../../utils/toSentenceCase';

export { formatThemeText, getHeadingTextTransform, toSentenceCase, usesSentenceCase };

// Fallback used whenever a theme record (default, DB-stored custom theme, or an older
// snapshot saved before `casing` existed) doesn't define its own casing — preserves the
// legacy all-caps look so existing themes render unchanged.
export const DEFAULT_CASING = { heading: 'uppercase', label: 'uppercase' };

// Merges a theme's `casing` (if any) over DEFAULT_CASING so callers always get both keys,
// even for theme records that predate this field or only define part of it.
export const MARSH_CASING = { heading: 'none', label: 'none' };

export const resolveCasing = (theme) => {
  if (isMarshTheme(theme)) return { ...MARSH_CASING };
  return {
    ...DEFAULT_CASING,
    ...(theme && typeof theme === 'object' ? theme.casing : undefined),
  };
};

// DB Marsh theme records may predate `casing` or still carry legacy h1.textTransform:
// 'capitalize', which title-cases every word. Detect Marsh by its JSON fingerprint so
// sentence case works even before the server-side theme record is updated.
export const isMarshTheme = (theme) => {
  if (!theme || typeof theme !== 'object' || 'colorBg1' in theme) return false;
  const coverBg = theme.covers?.[0]?.bgColor?.[0];
  return (
    theme.headings?.h1?.background === 'transparent' &&
    typeof coverBg === 'string' &&
    coverBg.toUpperCase() === '#000F47'
  );
};

// Marsh 2026 brand values — see docs/marsh-ribbon-styles/README.md
export const marshTheme = () => ({
  covers: [
    {
      bgColor: ['#000F47', '#000F47'],
    },
  ],
  body: {
    color: '#000F47',
    font: 'Arial, sans-serif',
    size: '15px',
  },
  link: {
    color: '#0B4BFF', // Marsh Blue 750 (Active)
    active: '#0B4BFF',
    hover: '#0B4BFF',
    visited: '#7B7974',
  },
  // Marsh brand uses sentence case throughout — headings, page header/footer, table labels —
  // never forced ALL CAPS. Content should be authored in sentence case; this just stops any
  // CSS from re-casing it.
  casing: { ...MARSH_CASING },
  headings: {
    h1: {
      color: '#000F47',
      font: "Merriweather, Gelasio, 'Times New Roman', serif",
      weight: 300, // Merriweather Light — closest free match to Georgia Pro Light
      size: '2em',
      background: 'transparent', // plain text page headings, no banner (like AON)
    },
    h2: {
      color: '#000F47',
      font: "Merriweather, Gelasio, 'Times New Roman', serif",
      weight: 300,
      size: '1.5em',
    },
    callout: {
      color: '#000F47',
      font: "Merriweather, Gelasio, 'Times New Roman', serif",
      weight: 300,
    },
  },
  visuals: {
    divider: {
      color: '#CEECFF',
      width: '2px',
    },
  },
});

export const defaultTheme = (theme) => ({
  covers: [
    {
      bgColor: [theme.colorBg1, theme.colorBg2],
    },
  ],
  body: {
    color: 'rgba(0, 0, 0, 0.8)',
    font: "Roboto, 'Liberation Sans', Arial, sans-serif",
    size: '15px',
  },
  link: {
    color: '#f1c400',
    active: '#f1c400',
    hover: '#f1c400',
    visited: '#f1c400',
  },
  casing: { ...DEFAULT_CASING },
  headings: {
    h1: {
      color: '#fff',
      font: "Roboto, 'Liberation Sans', Arial, sans-serif",
      size: '2em',
      background: theme.colorBg1,
    },
    h2: {
      color: theme.colorTitle,
      font: "Roboto, 'Liberation Sans', Arial, sans-serif",
      size: '1.5em',
    },
    pageHeading: {
      color: '#fff',
    },
    coverTitle: theme.colorTitle,
  },
  visuals: {
    divider: {
      color: theme.colorBar,
      width: '2px', // Default width
    },
  },
});

export default function useTheme(ctx) {
  const theme = useContext(ctx);
  // return marshTheme(); // TESTING ONLY — remove before commit
  // eslint-disable-next-line no-unreachable
  const resolved = 'colorBg1' in theme ? defaultTheme(theme) : theme;
  const isMarsh = isMarshTheme(resolved);
  // Guarantees `casing` exists even for DB-stored theme records (Marsh, AON, custom)
  // saved before this field was introduced, or missing one of its two keys.
  if (!isMarsh) return { ...resolved, casing: resolveCasing(resolved), isMarsh };

  // Ensure Marsh body/heading colours stay on-brand even when the DB theme JSON is
  // incomplete or still carries legacy grey/black body text.
  const marsh = marshTheme();
  return {
    ...marsh,
    ...resolved,
    body: { ...marsh.body, ...(resolved.body || {}), color: marsh.body.color },
    link: { ...marsh.link, ...(resolved.link || {}) },
    headings: {
      ...marsh.headings,
      ...(resolved.headings || {}),
      h1: { ...marsh.headings.h1, ...(resolved.headings?.h1 || {}), color: marsh.headings.h1.color },
      h2: { ...marsh.headings.h2, ...(resolved.headings?.h2 || {}), color: marsh.headings.h2.color },
      callout: {
        ...marsh.headings.callout,
        ...(resolved.headings?.callout || {}),
        color: marsh.headings.callout.color,
      },
    },
    visuals: {
      ...marsh.visuals,
      ...(resolved.visuals || {}),
      divider: { ...marsh.visuals.divider, ...(resolved.visuals?.divider || {}) },
    },
    casing: resolveCasing(resolved),
    isMarsh: true,
  };
}
